import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { TransferErrorPopupComponent } from '@shared/components/transfer-error-popup/transfer-error-popup.component';
import { UploadStatus } from '@shared/models/fileupload';
import { PalletisingConfigTransferService } from '@shared/services/export-import-services/palletising-config';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { NotificationService } from '@shared/services/notification.service';
import { Subject, takeUntil } from 'rxjs';
import { CreateUpdateLayerParamDialogComponent } from './create-update-layer-param/create-update-layer-param';
import { CreateUpdateStackParamDialogComponent } from './create-update-stack-param/create-update-stack-param';
import { PalletisingConfigurationService } from './services/palletising.configuration.service';
import { ILayerParam, ILayerRowAction, IPalletTypeItem, IRowAction, IStackParam } from './types';

@Component({
	selector: 'app-palletising-configuration',
	templateUrl: './palletising.configuration.component.html',
	styleUrls: ['./palletising.configuration.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class PalletisingConfigurationComponent implements OnInit, OnDestroy {
	private unsubscribe = new Subject<void>();

	public favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_reset',
			text: () => 'masterdata.common.resetAllChanges',
			click: () => this.resetConfiguration(),
			disabled: () => this.service.viewModel.isResetButtonDisabled,
		},
		{
			icon: () => 'acies_save',
			text: () => 'masterdata.common.saveAllChanges',
			click: () => this.saveConfiguration(),
			disabled: () => this.service.viewModel.isSaveButtonDisabled,
		},
	];

	public menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_export',
			text: () => 'masterdata.palletising.configurationImportExport.export.button',
			click: () => this.onExport(),
			// Export also resolves palletTypeCombinations against a pallet-types roster
			// (PalletisingConfigTransferService.exportData -> fetchPalletTypes()), unlike carting-config's
			// export - so it needs PalletTypeList too, not just ConfigurationList.
			permissions: () => ['palletising:ConfigurationList', 'palletising:PalletTypeList'],
		},
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.palletising.configurationImportExport.import.button',
			click: () => this.onImport(),
			// Import validates/resolves the PalletTypeCombinations sheet against the same roster
			// (fetchPalletTypes()), in addition to reading the existing configuration (ConfigurationList)
			// and saving over it (ConfigurationPut).
			permissions: () => ['palletising:ConfigurationPut', 'palletising:ConfigurationList', 'palletising:PalletTypeList'],
		},
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.palletising.configurationImportExport.import.template.button',
			click: () => this.palletisingConfigTransferService.downloadTemplate(),
			// downloadTemplate() writes a local example file with no network calls, so ConfigurationList
			// alone (matching every sibling entity's template-button gating) is enough.
			permissions: () => ['palletising:ConfigurationList'],
		},
	];

	public constructor(
		public dialog: MatDialog,
		public service: PalletisingConfigurationService,
		private notificationService: NotificationService,
		private palletisingConfigTransferService: PalletisingConfigTransferService,
		private importValidatorService: ImportValidatorService,
	) {}

	public ngOnInit(): void {
		this.service.loadConfiguration();
	}

	public updatePriorityFormValidity(): void {
		this.service.updatePriorityFormValidity();
	}

	public updateConfiguration(): void {
		this.service.updateConfiguration();
	}

	public saveConfiguration(): void {
		this.service.saveConfiguration();
	}

	public resetConfiguration(): void {
		this.service.loadConfiguration();
	}

	public openCreateUpdateStackParam(data: IStackParam | null = null): void {
		this.dialog.open(CreateUpdateStackParamDialogComponent, {
			disableClose: true,
			width: '500px',
			data,
		});
	}

	public openCreateUpdateLayerParam(data: unknown | null = null): void {
		this.dialog.open(CreateUpdateLayerParamDialogComponent, {
			disableClose: true,
			width: '500px',
			data,
		});
	}

	public handleRowMenuAction(object: object): void {
		const row = object as IRowAction;
		if (row.action.handler === 'editStackParam') {
			this.openCreateUpdateStackParam(row.data);
		} else {
			this.onDeleteStackParam(row.data);
		}
	}

	public handleLayerRowMenuAction(object: object): void {
		const row = object as ILayerRowAction;
		if (row.action.handler === 'editLayerParam') {
			this.openCreateUpdateLayerParam(row.data);
		} else {
			this.onDeleteLayerParam(row.data);
		}
	}

	private onDeleteStackParam(param: IStackParam): void {
		this.service.onDeleteStackParam(param);
	}

	private onDeleteLayerParam(param: ILayerParam): void {
		this.service.ondDeleteLayerParam(param);
	}

	public palletTypeSelected(item: IPalletTypeItem): void {
		this.service.palletTypeSelected(item);
	}

	public onExport(): void {
		const config = this.service.serverConfiguration;

		this.palletisingConfigTransferService
			.exportData(config ? [config] : [])
			.pipe(takeUntil(this.unsubscribe))
			.subscribe({
				next: () => {
					this.notificationService.notify(['masterdata.palletising.configurationImportExport.export.notification.success.header'], ['masterdata.palletising.configurationImportExport.export.notification.success.content'], 'success');
				},
				error: (error: Error) => {
					console.error('Failed to export palletising configuration', error);
					this.notificationService.notify(['masterdata.palletising.configurationImportExport.export.notification.error.header'], ['masterdata.palletising.configurationImportExport.export.notification.error.content'], 'error');
					this.dialog.open(TransferErrorPopupComponent, {
						width: '450px',
						data: { message: error.message },
					});
				},
			});
	}

	public onImport(): void {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'masterdata.palletising.configurationImportExport.import.title',
				subHeadingKey: 'masterdata.palletising.configurationImportExport.import.subtitle',
				confirmButtonKey: 'masterdata.palletising.configurationImportExport.import.button',
				notificationHeader: 'masterdata.palletising.configurationImportExport.import.notification.success.header',
				notificationContent: 'masterdata.palletising.configurationImportExport.import.notification.success.content',
				notificationErrorHeader: 'masterdata.palletising.configurationImportExport.import.notification.error.header',
				notificationErrorContent: 'masterdata.palletising.configurationImportExport.import.notification.error.content',
				notificationClientErrorHeader: 'masterdata.palletising.configurationImportExport.import.notification.clientError.header',
				notificationClientErrorContent: 'masterdata.palletising.configurationImportExport.import.notification.clientError.content',
				detailedClientErrorMessage: 'fileuploader.detailed-client-error-message',
				allowedFileTypes: ['.xlsx'],
				validatorService: this.importValidatorService,
				data: this.palletisingConfigTransferService,
			},
		});

		// afterClosed() also emits for Cancel (undefined - see FileuploadComponent.onNoClick) and for a
		// closed-but-failed import (UploadStatus with status 'error'/'client_error'), so reloading
		// unconditionally would discard any unsaved form edits even though nothing was actually saved.
		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((result?: UploadStatus) => {
				if (result?.status === 'success') {
					this.service.loadConfiguration();
				}
			});
	}

	public ngOnDestroy(): void {
		this.service.palletTypeSelected(null);
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}
}
